import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { Button, Stack, Box, Typography, Alert } from "@mui/material";
import {
  getScheduleJobDetails,
  updateScheduleJob,
} from "../../api/services/scheduling";
import { QK } from "../../base/qk";
import Loader from "../Loader/Loader";
import CustomTimePicker from "../FormFields/TiimePicker/TimePicker";

const ChangeJobTime = ({ id, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: JobDetails, isLoading } = useQuery(
    ["job-details", id],
    () => getScheduleJobDetails(id),
    {
      enabled: !!id,
    }
  );

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (JobDetails) {
      const rawStart = JobDetails?.rescheduled_start || JobDetails?.scheduled_start;
      const rawEnd = JobDetails?.rescheduled_end || JobDetails?.scheduled_end;

      const formatTime = (timeStr) => {
        if (!timeStr) return "";
        return timeStr.substring(0, 5);
      };

      setStartTime(formatTime(rawStart));
      setEndTime(formatTime(rawEnd));

      const status = JobDetails?.status;
      const completedStatuses = [4, 6];
      setIsDisabled(completedStatuses.includes(status));
    }
  }, [JobDetails]);

  const handleSave = async () => {
    const updatedData = {
      rescheduled_start: `${startTime}:00`,
      rescheduled_end: `${endTime}:00`,
    };

    try {
      const response = await updateScheduleJob(id, updatedData);
      if (response.status === 200) {
        enqueueSnackbar("Time updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.scheduledRunVisits]);
        queryClient.invalidateQueries([QK.scheduleTimeline]);
        if (onSuccess) onSuccess();
      } else {
        enqueueSnackbar("Update failed", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Box sx={{ position: 'relative', zIndex: 10000 }}>
      {isDisabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This visit is already <strong>{JobDetails?.status_name}</strong>. Time cannot be modified.
        </Alert>
      )}
      
      <Stack spacing={3}>
        <CustomTimePicker
          label="Start Time"
          name="startTime"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          required
          disabled={isDisabled}
        />

        <CustomTimePicker
          label="End Time"
          name="endTime"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          required
          disabled={isDisabled}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={!startTime || !endTime || isDisabled}
          >
            Save Changes
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ChangeJobTime;